<template>
  <section class="hero-section">
    <!-- Background layers -->
    <div class="hero-bg" aria-hidden="true">
      <!-- Builder tenants can upload a hero background; a dark scrim keeps
           the copy readable on any image. Absent (the default) nothing
           renders and the canvas/mesh stack below carries the visuals. -->
      <div
        v-if="props.backgroundImage"
        class="hero-bg-image"
        :style="{ backgroundImage: `url(${props.backgroundImage})` }"
      ></div>
      <ClientOnly>
        <HeroCanvas />
      </ClientOnly>
      <div class="hero-mesh"></div>
      <div class="orb orb-cyan"></div>
      <div class="orb orb-coral"></div>
      <div class="hero-stars"></div>
    </div>

    <!-- Content — flex-centered against the full viewport; the foot
         (trustline + marquee) sits in normal flow below, so nothing is
         absolutely positioned into the copy on short screens. -->
    <div class="hero-main">
      <div class="hero-content">
        <!-- Brand mark. The 3D logo pulls Three.js + a 6.4 MB .glb + the Draco
             decoder and runs a continuous WebGL loop — a multi-megabyte, jank-
             inducing cost on phones for a decorative flourish. So it loads ONLY
             on capable desktops, and only after the browser goes idle; every
             phone and every reduced-motion visitor gets a weightless static
             mark instead (zero Three.js, zero model download). -->
        <div v-if="isDiztree" class="hero-logo3d">
          <ClientOnly>
            <DiztreeLogo3D v-if="use3DLogo" :rotate-speed="0.4" :fit-size="2.8" />
            <img
              v-else
              src="/android-chrome-512x512.png"
              alt=""
              class="hero-logo-static"
              :class="{ 'is-spin': !reducedMotion }"
              width="512"
              height="512"
              fetchpriority="high"
            />
            <template #fallback>
              <img src="/android-chrome-512x512.png" alt="" class="hero-logo-static" width="512" height="512" />
            </template>
          </ClientOnly>
        </div>

        <div class="hero-pill">
          <span class="pill-dot"></span>
          {{ $t('landing.hero_badge') }}
        </div>

        <h1 class="hero-title">
          <span class="title-line-1">{{ props.titleLine1 || $t('landing.hero_title_1') }}</span>
          <span class="title-line-2">{{ props.titleLine2 || $t('landing.hero_title_2') }}</span>
        </h1>

        <p class="hero-subtitle">
          {{ props.subtitle || $t('landing.hero_subtitle') }}
        </p>

        <div class="hero-buttons">
          <NuxtLink :to="props.ctaLink || (localePath('/auth') + '?tab=signup')" class="btn-cyber-primary btn-hero">
            {{ props.ctaText || $t('landing.hero_cta_distribute') }}
          </NuxtLink>
          <!-- Secondary, low-commitment path. Hidden unless a text is passed,
               so builder tenants keep today's single-CTA hero. -->
          <a
            v-if="props.secondaryCtaText"
            :href="props.secondaryCtaLink || '#how-it-works'"
            class="btn-cyber-secondary btn-hero"
          >
            {{ props.secondaryCtaText }}
          </a>
        </div>

        <!-- Risk-reduction facts straight under the CTAs (opt-in) -->
        <ul v-if="props.showHeroFacts" class="hero-facts">
          <li v-for="fact in heroFacts" :key="fact" class="hero-fact">
            <span class="fact-check" aria-hidden="true">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            {{ fact }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Foot: scroll hint, live trust counts, DSP marquee — all in flow -->
    <div class="hero-foot">
      <div class="scroll-indicator" aria-hidden="true">
        <div class="scroll-line"></div>
      </div>

      <!-- Compact social proof above the fold (opt-in; hides itself when the
           showcase API has no data — no zero-tiles, C5). -->
      <div v-if="props.showTrustline && hasTrust" class="hero-trustline">
        <span class="tl-item">
          <strong>{{ fmt(trustTotals.labels) }}</strong> {{ $t('landing.trust_stat_labels') }}
        </span>
        <span class="tl-item">
          <strong>{{ fmt(trustTotals.artists, true) }}</strong> {{ $t('landing.trust_stat_artists') }}
        </span>
        <span class="tl-item">
          <strong>{{ fmt(trustTotals.releases, true) }}</strong> {{ $t('landing.trust_stat_releases') }}
        </span>
      </div>

      <!-- DSP Logo marquee -->
      <div v-if="props.showDspMarquee !== false" class="logo-marquee">
        <div class="marquee-fade-left"></div>
        <div class="marquee-track">
          <div class="marquee-content">
            <span v-for="i in 2" :key="i" class="logo-set">
              <span
                v-for="dsp in dsps"
                :key="dsp.name + i"
                class="dsp-logo"
                :style="{ '--dsp-color': dsp.color }"
              >
                <NuxtIcon :name="dsp.icon" class="dsp-icon" />
                <span class="dsp-name">{{ dsp.name }}</span>
              </span>
            </span>
          </div>
        </div>
        <div class="marquee-fade-right"></div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { defineAsyncComponent } from 'vue'
import HeroCanvas from '~/components/Home/HeroCanvas.vue'
// Lazy-load Three.js + GLTF/Draco loaders only when the 3D logo actually
// renders. Static import dragged ~600 KB gzipped of three/examples into
// the landing-page chunk even though the component is wrapped in
// <ClientOnly> and only mounts on the homepage.
const DiztreeLogo3D = defineAsyncComponent(() => import('~/components/Home/DiztreeLogo3D.vue'))

const props = defineProps({
  titleLine1: String,
  titleLine2: String,
  subtitle: String,
  ctaText: String,
  ctaLink: String,
  backgroundImage: String,
  showDspMarquee: { type: Boolean, default: true },
  // Additive props (default = legacy behavior) so builder-tenant heroes
  // render exactly as before unless a page opts in explicitly.
  secondaryCtaText: String,
  secondaryCtaLink: String,
  showHeroFacts: { type: Boolean, default: false },
  showTrustline: { type: Boolean, default: false }
})

const localePath = useLocalePath()
const { tenant } = useTenantTheme()
const { t } = useI18n()

const dsps = [
  { name: 'Spotify', icon: 'simple-icons:spotify', color: '#1DB954' },
  { name: 'Apple Music', icon: 'simple-icons:applemusic', color: '#FA233B' },
  { name: 'Tidal', icon: 'simple-icons:tidal', color: '#ffffff' },
  { name: 'Deezer', icon: 'simple-icons:deezer', color: '#A238FF' },
  { name: 'Amazon Music', icon: 'simple-icons:amazonmusic', color: '#25D1DA' },
  { name: 'YouTube Music', icon: 'simple-icons:youtubemusic', color: '#FF0000' },
  { name: 'SoundCloud', icon: 'simple-icons:soundcloud', color: '#FF5500' },
  { name: 'TikTok', icon: 'simple-icons:tiktok', color: '#FF0050' },
  { name: 'Instagram', icon: 'simple-icons:instagram', color: '#E4405F' },
  // arcticons, not simple-icons: simple-icons has no Qobuz glyph (the API
  // returns not_found, which Nuxt then caches — hence the repeating
  // "[Icon] failed to load icon" warning this replaced). arcticons is the
  // only set carrying one, and it is monochrome `currentColor` like the
  // others, so it inherits the rest-state tint and the hover glow. It is
  // stroke-drawn rather than solid, so it reads a touch lighter than its
  // neighbours at 1em — visible only if you look for it.
  { name: 'Qobuz', icon: 'arcticons:qobuz', color: '#4DB4E0' }
]
const isDiztree = computed(() => {
  const slug = tenant.value?.slug
  return !slug || slug === 'default' || slug === 'diztree'
})

const heroFacts = computed(() => [
  t('landing.hero_fact_royalties'),
  t('landing.hero_fact_platforms'),
  t('landing.hero_fact_speed'),
  t('landing.hero_fact_codes')
])

// ── Trustline (opt-in) ───────────────────────────────────────────────────
// Same endpoint + key as LandingTrust, so the payload is shared and the
// request happens once. Fetched only when the page opted in via prop —
// builder tenants don't pay for it.
let trustData = ref(null)
if (props.showTrustline) {
  const { data } = await useFetch('/api/public/showcase', {
    key: 'landing-showcase',
    default: () => ({ totals: { labels: 0, artists: 0, releases: 0 } })
  })
  trustData = data
}
const trustTotals = computed(() => trustData.value?.totals || { labels: 0, artists: 0, releases: 0 })
const hasTrust = computed(() =>
  (trustTotals.value.labels || 0) > 0
  || (trustTotals.value.artists || 0) > 0
  || (trustTotals.value.releases || 0) > 0
)

// Same round-and-suffix formatting as LandingTrust: exact small numbers,
// "1.8K+" style for large ones.
function fmt(n, approx = false) {
  const v = Number(n) || 0
  if (!approx || v < 1000) return String(v)
  if (v < 10000) return (Math.floor(v / 100) / 10).toFixed(1).replace(/\.0$/, '') + 'K+'
  return Math.floor(v / 1000) + 'K+'
}

// ── 3D-logo gating (mobile performance) ──────────────────────────────────
// `use3DLogo` stays false through SSR and the first client frame, so the
// heavy chunk is NEVER in the critical path. On mount we upgrade to the 3D
// mark only when the device can afford it: a desktop-width viewport, motion
// allowed, and — as a proxy for weak hardware — at least 4 logical cores.
// The import is deferred to requestIdleCallback so it never competes with
// first paint or hydration.
const use3DLogo = ref(false)
const reducedMotion = ref(false)

onMounted(() => {
  if (typeof window === 'undefined') return
  reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const capable =
    window.matchMedia('(min-width: 769px)').matches
    && !reducedMotion.value
    && (navigator.hardwareConcurrency == null || navigator.hardwareConcurrency >= 4)
  if (!capable) return

  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300))
  idle(() => { use3DLogo.value = true })
})
</script>

<style scoped>
/* Mobile-first. Breakpoint ladder: 480 / 640 / 768 / 1024 / 1280
   (documented in assets/css/variables.css). */

.hero-section {
  position: relative;
  min-height: 100vh;   /* fallback */
  min-height: 100svh;  /* stable against iOS URL-bar collapse */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Background ── */
.hero-bg {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
}

.hero-bg-image::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgb(0 0 0 / 0.5), rgb(0 0 0 / 0.78));
}

.hero-mesh {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(at 0% 0%, rgb(var(--primary-channels) / 0.12) 0px, transparent 50%),
    radial-gradient(at 100% 0%, rgb(var(--secondary-channels) / 0.12) 0px, transparent 50%),
    radial-gradient(at 100% 100%, rgb(var(--primary-channels) / 0.06) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgb(var(--secondary-channels) / 0.06) 0px, transparent 50%);
}

.hero-mesh::after {
  content: '';
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background-image:
    linear-gradient(rgb(var(--secondary-channels) / 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgb(var(--secondary-channels) / 0.06) 1px, transparent 1px);
  background-size: 60px 60px;
  transform: perspective(500px) rotateX(60deg) translateY(100px) translateZ(-200px);
  opacity: 0.3;
}

/* Orbs: small, mildly blurred and STATIC on phones — blur repaints of
   600px circles are one of the most expensive things a phone GPU can be
   asked to do. Full size + drift only from tablet up. */
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.22;
}

.orb-cyan {
  top: -10%;
  left: 10%;
  width: 240px;
  height: 240px;
  background: var(--primary);
}

.orb-coral {
  bottom: -12%;
  right: 5%;
  width: 200px;
  height: 200px;
  background: var(--secondary, #ff6b6b);
  opacity: 0.16;
}

.hero-stars {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(1.5px 1.5px at 12% 28%, rgba(255, 255, 255, 0.8), transparent),
    radial-gradient(1px 1px at 78% 64%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1.2px 1.2px at 45% 88%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1px 1px at 88% 12%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1.4px 1.4px at 22% 72%, rgba(255, 255, 255, 0.55), transparent),
    radial-gradient(1px 1px at 60% 40%, rgba(255, 255, 255, 0.4), transparent),
    radial-gradient(1.2px 1.2px at 33% 18%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1px 1px at 92% 82%, rgba(255, 255, 255, 0.6), transparent);
  background-size: 100% 100%;
  opacity: 0.5;
  pointer-events: none;
}

@media (min-width: 768px) {
  .hero-mesh::after {
    animation: gridMove 20s linear infinite;
  }

  .orb {
    filter: blur(120px);
    opacity: 0.25;
    animation: float 20s infinite ease-in-out;
  }

  .orb-cyan {
    top: -15%;
    left: 15%;
    width: 600px;
    height: 600px;
    animation-delay: -5s;
  }

  .orb-coral {
    bottom: -20%;
    right: 10%;
    width: 480px;
    height: 480px;
    opacity: 0.18;
    animation-delay: -11s;
  }

  .hero-stars {
    opacity: 0.6;
    animation: starTwinkle 6s ease-in-out infinite;
  }
}

@keyframes gridMove {
  0% { transform: perspective(500px) rotateX(60deg) translateY(0) translateZ(-200px); }
  100% { transform: perspective(500px) rotateX(60deg) translateY(60px) translateZ(-200px); }
}

@keyframes starTwinkle {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.7; }
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  33% { transform: translate(30px, -20px); }
  66% { transform: translate(-20px, 15px); }
}

/* ── Content ── */
.hero-main {
  position: relative;
  z-index: 10;
  flex: 1 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  /* clear the fixed nav */
  padding-top: calc(64px + env(safe-area-inset-top, 0px));
}

.hero-content {
  width: 100%;
  max-width: 900px;
  text-align: center;
  padding: var(--space-4) var(--space-6) var(--space-8);
}

.hero-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px;
  border-radius: 9999px;
  border: 1px solid rgb(var(--primary-channels) / 0.25);
  background: rgb(var(--primary-channels) / 0.05);
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: clamp(var(--space-4), 3.5vw, var(--space-8));
  animation: heroEnter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
  text-shadow: 0 0 20px var(--primary-glow), 0 0 40px rgb(var(--primary-channels) / 0.3);
}

@media (min-width: 480px) {
  .hero-pill {
    font-size: 0.7rem;
    padding: 6px 18px;
  }
}

.pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary);
  box-shadow: 0 0 8px var(--primary);
  animation: pulse 4s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* Fluid logo — scales with viewport width AND height, so short desktop
   viewports (1280×800, 1024×768) keep the primary CTA above the fold. */
.hero-logo3d {
  width: min(clamp(96px, 28vw, 400px), 30vh);
  aspect-ratio: 1;
  margin: 0 auto var(--space-4);
  animation: heroEnter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Static brand mark shown wherever the 3D logo is skipped (mobile,
   reduced-motion, pre-idle). A cyan drop-shadow ties it to the neon
   identity; a slow spin echoes the 3D rotation without any WebGL cost —
   desktop-only, and dropped entirely under reduced-motion. */
.hero-logo-static {
  width: 74%;
  height: 74%;
  object-fit: contain;
  filter: drop-shadow(0 0 34px rgb(var(--primary-channels) / 0.45))
          drop-shadow(0 0 4px rgb(var(--primary-channels) / 0.3));
}
@media (min-width: 768px) {
  .hero-logo-static.is-spin {
    animation: heroLogoSpin 18s linear infinite;
  }
}
@keyframes heroLogoSpin {
  from { transform: rotateY(0deg); }
  to { transform: rotateY(360deg); }
}

/* Fluid type scale — no nowrap: long or translated titles wrap instead of
   pushing the page wide. */
.hero-title {
  font-family: var(--font-heading);
  font-size: clamp(2rem, 0.8rem + 6.6vw, 6.5rem);
  font-weight: 800;
  line-height: 1.04;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  text-align: center;
  width: 100%;
  margin: 0 auto var(--space-5);
  animation: heroEnter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both;
}

.title-line-1 {
  display: block;
  text-wrap: balance;
  color: #fff;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.9), 0 0 40px rgba(0, 0, 0, 0.8), 0 0 80px rgba(0, 0, 0, 0.5);
}

.title-line-2 {
  display: block;
  text-wrap: balance;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 40px rgba(0, 0, 0, 0.5));
}

.hero-subtitle {
  font-size: clamp(0.98rem, 0.9rem + 0.5vw, 1.2rem);
  max-width: 560px;
  margin: 0 auto var(--space-6);
  line-height: 1.65;
  animation: heroEnter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.6s both;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.9), 0 0 40px rgba(0, 0, 0, 0.7);
}

@media (min-width: 768px) {
  .hero-subtitle {
    margin-bottom: var(--space-8);
  }
}

.hero-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  animation: heroEnter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.8s both;
}

.btn-hero {
  height: 48px !important;
  padding: 0 32px !important;
  font-size: 0.75rem !important;
  width: 100%;
  max-width: 300px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.7), 0 0 40px rgba(0, 0, 0, 0.4) !important;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

@media (min-width: 480px) {
  .hero-buttons {
    flex-direction: row;
    justify-content: center;
    gap: var(--space-4);
  }

  .btn-hero {
    width: auto;
  }
}

/* Facts row under the CTAs */
.hero-facts {
  list-style: none;
  margin: var(--space-5) auto 0;
  padding: 0;
  max-width: 640px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 16px;
  animation: heroEnter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.9s both;
}

.hero-fact {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--text-muted);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  white-space: nowrap;
}

.fact-check {
  display: inline-flex;
  color: var(--primary);
  flex-shrink: 0;
}

@keyframes heroEnter {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ── Foot (in flow — no absolute positioning colliding with content) ── */
.hero-foot {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
  padding-bottom: calc(var(--space-4) + env(safe-area-inset-bottom, 0px));
}

.scroll-indicator {
  display: none;
  animation: heroEnter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 1.2s both;
}

/* Decorative — only where there's room for it */
@media (min-width: 768px) and (min-height: 600px) {
  .scroll-indicator {
    display: block;
  }
}

.scroll-line {
  width: 1px;
  height: 34px;
  background: linear-gradient(to bottom, var(--primary), transparent);
  animation: scrollBob 2s ease-in-out infinite;
}

@keyframes scrollBob {
  0%, 100% { opacity: 0.6; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(8px); }
}

/* Trustline */
.hero-trustline {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 22px;
  padding: 0 var(--space-6);
  font-size: 0.8rem;
  color: var(--text-muted);
  animation: heroEnter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 1s both;
}

.tl-item {
  white-space: nowrap;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
}

.tl-item strong {
  font-family: var(--font-heading);
  font-weight: 800;
  color: var(--text-main);
  font-variant-numeric: tabular-nums;
  margin-right: 2px;
}

/* ── DSP Logo Marquee ── */
.logo-marquee {
  position: relative;
  width: 100%;
  overflow: hidden;
  animation: heroEnter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 1s both;
}

.marquee-fade-left,
.marquee-fade-right {
  position: absolute;
  top: 0;
  width: 60px;
  height: 100%;
  z-index: 2;
  pointer-events: none;
}

.marquee-fade-left {
  left: 0;
  background: linear-gradient(to right, var(--bg-color), transparent);
}

.marquee-fade-right {
  right: 0;
  background: linear-gradient(to left, var(--bg-color), transparent);
}

/* Phones: a static, swipeable strip — no infinite repaint loop. */
.marquee-track {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.marquee-track::-webkit-scrollbar {
  display: none;
}

.marquee-content {
  display: flex;
  width: max-content;
  padding: 2px var(--space-4);
}

.logo-set {
  display: flex;
  gap: 1rem;
  padding-right: 1rem;
}

/* the duplicate set only exists for the seamless desktop loop */
.logo-set:nth-child(2) {
  display: none;
}

@media (min-width: 768px) {
  .marquee-fade-left,
  .marquee-fade-right {
    width: 120px;
  }

  .marquee-track {
    overflow: hidden;
  }

  .marquee-content {
    animation: marquee 40s linear infinite;
    padding: 2px 0;
  }

  .logo-set:nth-child(2) {
    display: flex;
  }
}

/* Reduced-motion: swipeable static strip on every width (the global
   freeze in main.css would otherwise clip the loop mid-frame). */
@media (prefers-reduced-motion: reduce) {
  .marquee-track {
    overflow-x: auto;
  }

  .marquee-content {
    animation: none;
    padding: 2px var(--space-4);
  }

  .logo-set:nth-child(2) {
    display: none;
  }
}

.dsp-logo {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  font-family: var(--font-heading);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  transition:
    color 0.25s ease,
    background 0.25s ease,
    border-color 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.dsp-icon {
  display: inline-flex;
  align-items: center;
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.65);
  transition: color 0.25s ease, filter 0.25s ease;
}

.dsp-logo :deep(svg) {
  display: block;
  width: 1em;
  height: 1em;
}

.dsp-logo:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
  border-color: color-mix(in srgb, var(--dsp-color) 60%, transparent);
  transform: translateY(-1px);
  box-shadow:
    0 6px 18px color-mix(in srgb, var(--dsp-color) 25%, transparent),
    0 0 0 1px color-mix(in srgb, var(--dsp-color) 35%, transparent) inset;
}

.dsp-logo:hover .dsp-icon {
  color: var(--dsp-color);
  filter: drop-shadow(0 0 6px var(--dsp-color));
}

@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

/* Short DESKTOP viewports (1280×800, 1024×768): tighten the rhythm so the
   primary CTA stays above the fold without shrinking the type. */
@media (min-width: 768px) and (max-height: 830px) {
  .hero-pill {
    margin-bottom: var(--space-4);
  }

  .hero-title {
    margin-bottom: var(--space-4);
  }

  .hero-subtitle {
    margin-bottom: var(--space-5);
  }

  .hero-logo3d {
    margin-bottom: var(--space-3);
  }
}

/* Phone landscape: compress the vertical rhythm so the primary CTA stays
   above the fold. Landscape-only — tiny PORTRAIT phones (320×568) must
   keep the fluid vw type scale or the title overshoots the width. */
@media (max-height: 600px) and (orientation: landscape) {
  .hero-logo3d {
    width: clamp(56px, 14vh, 110px);
    margin-bottom: var(--space-2);
  }

  /* decorative badge — the first thing to go when height is scarce */
  .hero-pill {
    display: none;
  }

  .hero-title {
    font-size: clamp(1.7rem, 8vh, 2.6rem);
    margin-bottom: var(--space-3);
  }

  .hero-subtitle {
    font-size: 0.95rem;
    margin-bottom: var(--space-4);
  }

  .hero-content {
    padding-top: var(--space-2);
    padding-bottom: var(--space-4);
  }

  .scroll-indicator {
    display: none;
  }
}
</style>
